import { ColumnApi, GetRowIdFunc, GridApi, GridReadyEvent } from '@ag-grid-community/core';
import { Loader, Widget, AGTable as Table } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import isEmpty from 'lodash/isEmpty';
import React, { ReactElement, useCallback, useMemo, useRef, useState } from 'react';
import useFetchRecentChange from '../../../../hooks/riskProducts/dashboard/useFetchRecentChange';
import {
  RecentChangeListItemDto,
  RecentChangeMetric,
} from '../../../../services/dtos/riskProducts/dashboard/recentChangeList.dto';
import { WIDGET_ID } from '../../../../services/dtos/riskProducts/widgetMetric.dto';
import NoResults from '../../../NoResults';
import MetricsDropdown from '../MetricsDropdown';

import { DEFAULT_SORT_FIELD, DEFAULT_SORT_ORDER, getRecentChangesColumnDef } from './RecentChange.constants';

import styles from './RecentChange.module.scss';

const cx = classNames.bind(styles);

interface RecentChangesProps {
  watchListId: string;
}

const RecentChangeTable = Table<RecentChangeListItemDto>;
const RecentChange = ({ watchListId }: RecentChangesProps): ReactElement => {
  const gridApiRef = useRef<GridApi | null>(null);
  const columnApiRef = useRef<ColumnApi | null>(null);

  const [selectedCreditRiskMetric, setSelectedCreditRiskMetric] = useState<RecentChangeMetric | undefined>();
  const columnDefs = useMemo(
    () =>
      selectedCreditRiskMetric
        ? getRecentChangesColumnDef(selectedCreditRiskMetric, columnApiRef.current?.getColumnState())
        : null,
    [selectedCreditRiskMetric]
  );

  const { isError, isLoading, refetch, data: recentChanges } = useFetchRecentChange(watchListId);

  const handleGridReady = useCallback((event: GridReadyEvent) => {
    gridApiRef.current = event.api;
    columnApiRef.current = event.columnApi;
    event.columnApi.applyColumnState({ state: [{ colId: DEFAULT_SORT_FIELD, sort: DEFAULT_SORT_ORDER }] });
  }, []);

  const rowIdResolver = useCallback<GetRowIdFunc<RecentChangeListItemDto>>((params) => params.data.cs_companyid, []);

  const header = <div className={cx('heading')}>Recent RP Changes</div>;

  const renderFirstLine = () => {
    if (isError) {
      return undefined;
    }
    return (
      <div className={cx('actions')}>
        <MetricsDropdown
          widgetId={WIDGET_ID.RECENT_RP_CHANGES}
          value={selectedCreditRiskMetric}
          onOptionSelect={(value) => setSelectedCreditRiskMetric(value as RecentChangeMetric)}
          isLeftAligned
          dataTestId="recent-changes-select"
        />
      </div>
    );
  };

  const renderContent = () => {
    if (isEmpty(recentChanges)) {
      return <NoResults />;
    }
    return (
      <div className={cx('table-container')} data-testid="recent-rp-table">
        <RecentChangeTable
          onGridReady={handleGridReady}
          columnDefs={columnDefs}
          loadingOverlayComponent={Loader}
          getRowId={rowIdResolver}
          rowData={recentChanges}
        />
      </div>
    );
  };

  return (
    <Widget
      headerTitle={header}
      classNames={cx('body')}
      firstLine={renderFirstLine()}
      isError={isError}
      isLoading={isLoading}
      onRetryAfterErrorClick={refetch}
      dataTestId="recent-rp-widget"
    >
      {renderContent()}
    </Widget>
  );
};

export default RecentChange;
